import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import {
  getEmailCampaignPreview,
  getEmailCampaigns,
  updateEmailCampaign,
  type EmailCampaignIndexKeyEntries,
  type EmailCampaignOrganizationScope,
} from './emailCampaigns.api'
import type { EmailCampaignId, UpdateEmailCampaignInput } from './emailCampaigns.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries the fetcher reads; the scoped key builder below sets them. */
type EmailCampaignIndexQueryKey = [AdminQueryRoot & EmailCampaignIndexKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const emailCampaignQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'email-campaigns' }, { organizationId }),
  ],
  index: ({ organizationId }: OrganizationScope = {}): EmailCampaignIndexQueryKey =>
    buildScopedAdminQueryKey(emailCampaignQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
    }),
}

export const emailCampaignQueries = {
  index: ({ organizationId }: EmailCampaignOrganizationScope) =>
    queryOptions({
      queryKey: emailCampaignQueryKeys.index({ organizationId }),
      queryFn: getEmailCampaigns,
    }),
}

export function useEmailCampaigns({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof emailCampaignQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...emailCampaignQueries.index({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useUpdateEmailCampaignMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateEmailCampaignInput & { campaignId: EmailCampaignId }) =>
      updateEmailCampaign({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: emailCampaignQueryKeys.index({ organizationId }),
      }),
  })
}

export function useEmailCampaignPreviewMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ campaignId }: { campaignId: EmailCampaignId }) =>
      getEmailCampaignPreview({ organizationId, campaignId }),
  })
}
